import { MAX_WORDS_FOR_SCORE } from 'src/data/generation/const';
import { countWords } from 'src/lib/string';

export const BLOCKED_REASON = {
  maxWordsExceeded: 'MAX_WORDS_EXCEEDED',
  nonEnglish: 'NON_ENGLISH',
  invalidBrief: 'INVALID_BRIEF',
  limitedScoring: 'LIMITED_SCORING',
  emptyText: 'EMPTY_TEXT',
  invalidCampaignKeywordsAndProductName: 'INVALID_CAMPAIGN_KEYWORDS_AND_PRODUCT_NAME',
  performancePredictionsLimit: 'PERFORMANCE_PREDICTIONS_LIMIT',
} as const;
export type BlockedReason = (typeof BLOCKED_REASON)[keyof typeof BLOCKED_REASON];

const BLOCKED_REASON_COPY = {
  [BLOCKED_REASON.maxWordsExceeded]: `This text is too long to be scored.\nPlease edit your text to ${MAX_WORDS_FOR_SCORE} words or under.`,
  [BLOCKED_REASON.nonEnglish]: 'Scoring is available for English text only',
  [BLOCKED_REASON.limitedScoring]:
    'Get real-time scoring and analytics\nfor every copy edit you make',
  [BLOCKED_REASON.invalidBrief]:
    'To generate a score, describe your product/content in the section above.',
  [BLOCKED_REASON.emptyText]: 'Write something to see its predicted score and analytics',
  [BLOCKED_REASON.invalidCampaignKeywordsAndProductName]:
    'Campaign keywords and product name are needed to score copy',
  [BLOCKED_REASON.performancePredictionsLimit]:
    'You’ve reached your limit of performance predictions',
};

export const getBlockedReasonCopy = (blockedReason: BlockedReason) =>
  BLOCKED_REASON_COPY[blockedReason] || '';

export const getTextTooLongForScoringCopy = () =>
  getBlockedReasonCopy(BLOCKED_REASON.maxWordsExceeded);

export const isLockedScoreBlockedReason = (blockedReason: BlockedReason | undefined) => {
  const list: BlockedReason[] = [
    BLOCKED_REASON.limitedScoring,
    BLOCKED_REASON.performancePredictionsLimit,
  ];
  return blockedReason != null && list.includes(blockedReason);
};

export const isLockedInsightsBlockedReason = (blockedReason: BlockedReason) =>
  blockedReason && blockedReason !== BLOCKED_REASON.maxWordsExceeded;

export const ANYWORD_FACEBOOK_LINK =
  'https://www.facebook.com/groups/anyworders/?ref=anyword_dashboard';

export const ANYWORD_WEBINAR_URL = 'https://www.anyword.com/webinar-building-a-global-b2b-brand';

export const NAVIGATION_BAR_HEIGHT = 50; // TODO: move to src/data/ui/components.js + calculate whole top-bars height in UiStore to use in "views" useStyles

export const UPGRADE_DIALOG_ACTIONS_ORIGIN = {
  settingsPage: 'settings_page',
  analyticsPage: 'analytics_page',
  talkingPointsPage: 'talking_points_page',
  wtmPage: 'wtm_page',
  editorPage: 'editor_page',
  editorGetScoreButton: 'editor_get_score_button',
  generationFlow: 'generation_flow',
  brandVoicePage: 'brand_voice_page',
  cipPageConnectDialog: 'cip_page_connect_dialog',
  cipPageChannel: 'cip_page_channel',
  cipPageResource: 'cip_page_resource',
  cipPageWebsite: 'cip_page_website',
  workspaceDialog: 'workspace_dialog',
  customModel: 'custom_model',
  blogWizard: 'blog_wizard',
  targetAudiencePopup: 'target_audience_popup',
  brandTermsPage: 'brand_terms_page',
  performancePredictionsBanner: 'performance_predictions_banner',
  infinitySuggestions: 'infinity_suggestions',
  infinityVariations: 'infinity_variations',
  toneOfVoicePopup: 'tone_of_voice_popup',
} as const;
export type UpgradeDialogActionsOrigin =
  (typeof UPGRADE_DIALOG_ACTIONS_ORIGIN)[keyof typeof UPGRADE_DIALOG_ACTIONS_ORIGIN];

export const BOOK_DEMO_ORIGIN = {
  ...UPGRADE_DIALOG_ACTIONS_ORIGIN,
  scorePanelBrandTerms: 'score_panel_brand_terms',
  brandTermsPopup: 'brand_terms_popup',
  brandBriefsPage: 'brand_briefs_page',
  brandBriefsPopup: 'brand_briefs_popup',
  scoringMethodPopup: 'scoring_method_popup',
  subscriptionPage: 'subscription_page',
  customModelsPage: 'custom_models_page',
  TemplateBuilder: 'template_builder_page',
  targetAudiencePage: 'target_audience_page',
  customModels: 'copy_scoring_models',
  addWorkspace: 'add_workspace',
  addUser: 'add_user',
  toneOfVoicePage: 'custom_tone_of_voice_page',
  customerPersonaPopup: 'customer_persona_popup',
  checkPlagiarism: 'check_plagiarism',
  topNavigation: 'top_navigation',
} as const;
export type BookDemoOrigin = (typeof BOOK_DEMO_ORIGIN)[keyof typeof BOOK_DEMO_ORIGIN];

export const BOOK_DEMO_RESULT = {
  directedToBookDemo: 'directedToBookDemo',
  shownNotification: 'shownNotification',
  cancelled: 'cancelled',
} as const;
export type BookDemoResult = (typeof BOOK_DEMO_RESULT)[keyof typeof BOOK_DEMO_RESULT];

export const UPGRADE_ORIGIN = {
  subscriptionPage: 'subscription_page',
  scoringPopup: 'scoring_popup',
  topBanner: 'top_banner',
  navigationSidebar: 'navigation_sidebar',
} as const;
export type UpgradeOrigin = (typeof UPGRADE_ORIGIN)[keyof typeof UPGRADE_ORIGIN];

export const SORT_DIRECTION = {
  asc: 'asc',
  desc: 'desc',
} as const;
export type SortDirection = (typeof SORT_DIRECTION)[keyof typeof SORT_DIRECTION];

export const toggleSorting = <T extends string>(
  currentField: T,
  currentDirection: SortDirection,
  newField: T
) => {
  const sameField = currentField === newField;
  const sortedAsc = currentDirection === SORT_DIRECTION.asc;
  const newDirection = sameField && sortedAsc ? SORT_DIRECTION.desc : SORT_DIRECTION.asc;

  return {
    sortField: newField,
    sortDirection: newDirection,
  };
};

export const DEFAULT_TAXONOMY_NAME = 'General';
export const MAX_RESOURCE_PER_TYPE = 15;

export const textIsTooLongForScoring = (text: string) => {
  if (!text) {
    return false;
  }
  return countWords(text) > MAX_WORDS_FOR_SCORE;
};

export const UPLOADING_STATUS = {
  started: 'started',
  success: 'success',
  error: 'error',
} as const;
export type UploadingStatus = (typeof UPLOADING_STATUS)[keyof typeof UPLOADING_STATUS];

export const FIELD_TYPE = {
  text: 'text',
  image: 'image',
} as const;

// Until a store's first query settles its list is only the initial empty array. A failed query stays
// unloaded, so it counts as settled rather than leaving the caller waiting forever.
export const isInitialLoadPending = ({
  loading,
  loaded,
  error,
}: {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}): boolean => loading || (!loaded && !error);
